import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import UserService from 'service/UserService';
import { useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { UserCreateReq } from 'domain/payload/request/user/UserCreateReq';
import { UserUpdateReq } from 'domain/payload/request/user/UserUpdateReq';
import LoadingSpinner from 'component/LoadingSpinner';
import { encryptPassword } from 'helpers/EncryptPassword';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import UserDataScopeService from 'service/UserDataScopeService';

const UserForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);
  const navigate = useNavigate();

  const userService = UserService();
  const scopeService = UserDataScopeService();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<string[]>([]);

  const [roleOptions, setRoleOptions] = useState<{id: string; code: string; name: string }[]>([]);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const [scopeType, setScopeType] = useState<string>('');
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const [clientOptions, setClientOptions] = useState<{ id: string; code: string; name: string }[]>([]);
  const [projectOptions, setProjectOptions] = useState<{ id: string; code: string; name: string; client: string }[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingScope, setIsLoadingScope] = useState(false);

  const fetchClients = async () => {
    if (clientOptions.length > 0) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingClients(true);
    try {
      const clients = await scopeService.getClients(token);
      setClientOptions(
        clients.map((c) => ({ id: c.id, code: c.code, name: c.name }))
      );
    } catch (err: any) {
      console.error('Error fetching clients:', err);
      alert('Gagal memuat daftar client. Silakan coba lagi.');
    } finally {
      setIsLoadingClients(false);
    }
  };

  const fetchProjects = async () => {
    if (projectOptions.length > 0) return;

    const token = profile?.data.token;
    if (!token) return;

    setIsLoadingProjects(true);
    try {
      const projects = await scopeService.getProjects(token);
      setProjectOptions(
        projects.map((p) => ({
          id: p.id,
          code: p.code,
          name: p.name,
          client: p.client,
        }))
      );
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      alert('Gagal memuat daftar project. Silakan coba lagi.');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const handleScopeTypeChange = (newScopeType: string) => {
    setScopeType(newScopeType);
    
    if (newScopeType === 'CLIENT') {
      setSelectedProjectIds([]);
      fetchClients();
    } else if (newScopeType === 'PROJECT') {
      setSelectedClientIds([]);
      fetchProjects();
    } else {
      setSelectedClientIds([]);
      setSelectedProjectIds([]);
    }
  };

  const fetchUserScope = async () => {
    const token = profile?.data.token;
    if (!token || id === '0') return;
    setIsLoadingScope(true);
    try {
      const scope = await scopeService.getScopeDetail(token, id ?? "0");
      
      setScopeType((scope.scopeType || '').toUpperCase());
      
      if (scope.scopeType === 'CLIENT' && scope.clientScopes) {
        setSelectedClientIds(scope.clientScopes.map((c) => c.clientId));
        fetchClients();
      } else if (scope.scopeType === 'PROJECT' && scope.projectScopes) {
        setSelectedProjectIds(scope.projectScopes.map((p) => p.projectId));
        fetchProjects();
      }
    } catch (err: any) {
      console.error('Error fetching user scope:', err);
    } finally {
      setIsLoadingScope(false);
    }
  };

  const submitScope = async () => {
    const token = profile?.data.token;
    if (!token) throw new Error('Token tidak ditemukan');

    if (!scopeType) return;

    const request: any = {
      userId: id!,
      scopeType: scopeType,
    };

    if (scopeType === 'CLIENT') {
      if (selectedClientIds.length === 0) {
        throw new Error('Pilih minimal 1 client untuk scope Specified Clients');
      }
      request.clientIds = selectedClientIds;
    } else if (scopeType === 'PROJECT') {
      if (selectedProjectIds.length === 0) {
        throw new Error('Pilih minimal 1 project untuk scope Specified Projects');
      }
      request.projectIds = selectedProjectIds;
    }

    await scopeService.assignScope(token, request);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoadingSave(true);
    try {
      if (id === '0') {
        await userInvite();
        if (scopeType) {
          alert(`Data ${name} successfully created! Silakan edit kembali untuk mengatur scope.`);
        } else {
          alert(`Data ${name} successfully created!`);
        }
        navigate('/user');
      } else {
        await userUpdate();
        await submitScope();
        alert(`Data ${name} successfully updated!`);
        navigate('/user');
      }
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchRoleList = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const res = await userService.userConfig(token);
      const options = res.data?.roleList?.map((item: any) => ({
        id: item.id, 
        code: item.code,
        name: item.name,
      })) ?? [];
      setRoleOptions(options);
    } catch (err: any) {
      console.error("Error fetching role list:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil daftar role';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {
      const res = await userService.userDetail(token, id ?? "0");
      setName(res.data?.name || '');
      setPhone(res.data?.phone || '');
      setEmail(res.data?.email || '');
      if (res.data?.roles) setRole(res.data.roles.map((r: any) => r.id));
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail user';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const userInvite = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (initialPassword !== confirmPassword) {
      throw new Error("Password dan konfirmasi password tidak cocok");
    }

    const request: UserCreateReq = {
      name,
      phone,
      email,
      initialPassword: encryptPassword(initialPassword),
      roleIds: role,
    };
    await userService.userInvite(token, request);
  };

  const userUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const request: UserUpdateReq = {
      id: id!,
      name,
      phone,
      email,
      roleIds: role,
    };
    await userService.userUpdate(token, request);
  };

  useEffect(() => {
    fetchRoleList();
    if (id !== '0') {
      fetchDetail();
      fetchUserScope();
    }
  }, []);

  const computedRoleOptions = roleOptions.map((r) => ({ ...r, code: r.id }));
  const computedClientOptions = clientOptions.map((c) => ({
    ...c,
    code: c.id,
    label: `${c.name} (${c.code})`,
  }));
  const computedProjectOptions = projectOptions.map((p) => ({
    ...p,
    code: p.id,
    label: `${p.name} - ${p.client}`,
  }));

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Back Office User" : "Edit Back Office User"}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {isLoadingDetail ? (
            <LoadingSpinner />
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-sm-6">
                  <h5 className="mb-4">Basic Information</h5>

                  <div className="mb-3">
                    <label htmlFor="name" className="form-label fw-semibold">Name</label>
                    <input
                      type="text"
                      id="name"
                      className="form-control form-control-compact"
                      value={name}
                      placeholder="Name"
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label fw-semibold">Phone</label>
                    <input
                      type="text"
                      id="phone"
                      className="form-control form-control-compact"
                      value={phone}
                      placeholder="Phone Number"
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="roles" className="form-label fw-semibold">Roles</label>
                    <MultiSelectDropdown
                      options={computedRoleOptions}
                      value={role}
                      onChange={setRole}
                      placeholder="Select Roles"
                    />
                  </div>
                </div>

                <div className="col-sm-6">
                  <h5 className="mb-4">Credentials</h5>

                  <div className="mb-3">
                    <label htmlFor="email" className="form-label fw-semibold">Email</label>
                    <input
                      type="email"
                      id="email"
                      className="form-control form-control-compact"
                      value={email}
                      placeholder="Email"
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {id !== '0' && (
                <div className="row mt-4">
                  <div className="col-12">
                    <hr />
                    <h5 className="mb-3">Project Access Scope</h5>
                    <p className="text-muted small mb-3">
                      Tentukan project mana yang bisa diakses oleh user ini.
                    </p>

                    {isLoadingScope ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        {/* Radio Buttons */}
                        <div className="mb-3">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="scopeType"
                              id="scopeAll"
                              value="ALL"
                              checked={scopeType === 'ALL'}
                              onChange={(e) => handleScopeTypeChange(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="scopeAll">
                              <strong>All Projects</strong>
                              <div className="text-muted small">
                                User dapat mengakses semua project di sistem
                              </div>
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="scopeType"
                              id="scopeClient"
                              value="CLIENT"
                              checked={scopeType === 'CLIENT'}
                              onChange={(e) => handleScopeTypeChange(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="scopeClient">
                              <strong>Specified Clients</strong>
                              <div className="text-muted small">
                                User hanya dapat mengakses project milik client tertentu
                              </div>
                            </label>
                          </div>

                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="scopeType"
                              id="scopeProject"
                              value="PROJECT"
                              checked={scopeType === 'PROJECT'}
                              onChange={(e) => handleScopeTypeChange(e.target.value)}
                            />
                            <label className="form-check-label" htmlFor="scopeProject">
                              <strong>Specified Projects</strong>
                              <div className="text-muted small">
                                User hanya dapat mengakses project tertentu
                              </div>
                            </label>
                          </div>
                        </div>

                        {/* Conditional Rendering dengan Loading State */}
                        {scopeType === 'CLIENT' && (
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Select Clients</label>
                            {isLoadingClients ? (
                              <div className="d-flex align-items-center">
                                <LoadingSpinner />
                                <span className="ms-2 text-muted">Memuat daftar client...</span>
                              </div>
                            ) : (
                              <>
                                <MultiSelectDropdown
                                  options={computedClientOptions}
                                  value={selectedClientIds}
                                  onChange={setSelectedClientIds}
                                  placeholder="Pilih Client..."
                                />
                                <small className="text-muted">
                                  {selectedClientIds.length} client dipilih
                                </small>
                              </>
                            )}
                          </div>
                        )}

                        {scopeType === 'PROJECT' && (
                          <div className="mb-3">
                            <label className="form-label fw-semibold">Select Projects</label>
                            {isLoadingProjects ? (
                              <div className="d-flex align-items-center">
                                <LoadingSpinner />
                                <span className="ms-2 text-muted">Memuat daftar project...</span>
                              </div>
                            ) : (
                              <>
                                <MultiSelectDropdown
                                  options={computedProjectOptions}
                                  value={selectedProjectIds}
                                  onChange={setSelectedProjectIds}
                                  placeholder="Pilih Project..."
                                />
                                <small className="text-muted">
                                  {selectedProjectIds.length} project dipilih
                                </small>
                              </>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="w-100 d-flex justify-content-end">
                <Link to="/user">
                  <button type="button" className="btn btn-outline-primary">Cancel</button>
                </Link>
                <button
                  type="submit"
                  className="btn btn-primary ms-2"
                  disabled={isLoadingSave}
                >
                  {isLoadingSave && <LoadingSpinner reverse={true} />}
                  <span>Save</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserForm;